import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar as RBar, XAxis, Tooltip } from "recharts";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

const fmtMins = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);
const fmtDay = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
const fmtWhen = (iso) => new Date(iso).toLocaleString("en-US", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
const ago = (iso) => {
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
};
const Label = ({ children }) => <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-3">{children}</div>;

const Header = ({ d, onClose }) => (
    <div className="flex items-start gap-3">
        {d.user.picture ? <img src={d.user.picture} alt="" className="h-11 w-11 rounded-full object-cover" /> : <span className="h-11 w-11 rounded-full bg-[#2EB4AC] text-white font-bold flex items-center justify-center">{(d.user.name || "?")[0]}</span>}
        <div className="min-w-0 flex-1">
            <SheetTitle className="text-base font-bold text-slate-900 truncate" data-testid="usage-drawer-name">{d.user.name || d.user.email}</SheetTitle>
            <div className="text-xs text-slate-500 truncate">{d.user.email}{d.last_seen && ` · last seen ${ago(d.last_seen)}`}</div>
        </div>
        <button onClick={onClose} className="h-6 w-6 rounded-full border border-[#2EB4AC] text-[#2EB4AC] inline-flex items-center justify-center hover:bg-[#2EB4AC]/10" data-testid="usage-drawer-close" aria-label="Close"><X className="h-3.5 w-3.5" /></button>
    </div>
);

const Daily = ({ daily }) => (
    <div data-testid="usage-drawer-daily">
        <Label>Daily active time</Label>
        <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={daily} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                    <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fill: "#94a3b8", fontSize: 9 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                    <Tooltip cursor={{ fill: "rgba(46,180,172,0.08)" }} labelFormatter={fmtDay} formatter={(v) => [fmtMins(v), "Active"]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <RBar dataKey="minutes" fill="#2EB4AC" radius={[2, 2, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    </div>
);

const Modules = ({ modules }) => {
    const max = Math.max(1, ...modules.map((m) => m.minutes));
    return (
        <div data-testid="usage-drawer-modules">
            <Label>Most-used modules</Label>
            {modules.length === 0 ? <div className="text-xs text-slate-400">No activity in this period.</div> : modules.map((m) => (
                <div key={m.module} className="mb-3">
                    <div className="flex justify-between text-xs text-slate-800"><span>{m.module}</span><span className="text-slate-500 tabular-nums">{fmtMins(m.minutes)} · {m.views} views</span></div>
                    <div className="mt-1 h-1 rounded-full bg-slate-100"><div className="h-full rounded-full bg-[#2EB4AC]" style={{ width: `${Math.max(1, (m.minutes * 100) / max)}%` }} /></div>
                </div>
            ))}
        </div>
    );
};

const Recent = ({ recent }) => (
    <div data-testid="usage-drawer-recent">
        <Label>Recent activity <span className="normal-case tracking-normal font-normal">(last 200 page visits)</span></Label>
        {recent.length === 0 ? <div className="text-xs text-slate-400">No page visits yet.</div> : (
            <ol className="relative border-l border-[#2EB4AC]/30 ml-1">
                {recent.map((r, i) => (
                    <li key={i} className="pl-4 pb-3 relative">
                        <span className="absolute -left-[4px] top-1.5 h-[7px] w-[7px] rounded-full bg-[#2EB4AC]" />
                        <div className="text-sm text-slate-900">{r.module}</div>
                        <div className="text-[11px] text-slate-500 truncate">{fmtWhen(r.ts)} · <span className="font-mono">{r.path}</span></div>
                    </li>
                ))}
            </ol>
        )}
    </div>
);

export const UsageUserDrawer = ({ userId, range, onClose }) => {
    const [d, setD] = useState(null);
    useEffect(() => {
        if (!userId) return;
        setD(null);
        api.get(`/usage/user/${userId}`, { params: range })
            .then((r) => setD(r.data))
            .catch((e) => { toast.error(e?.response?.data?.detail || "Couldn't load user activity"); onClose(); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, range.start, range.end]);

    return (
        <Sheet open={!!userId} onOpenChange={(o) => !o && onClose()}>
            <SheetContent side="right" className="w-full sm:max-w-md bg-white text-slate-900 p-5 overflow-y-auto [&>button]:hidden" data-testid="usage-user-drawer">
                {!d ? <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-24 rounded-lg bg-slate-100 animate-pulse" />)}</div> : (
                    <div className="space-y-6">
                        <Header d={d} onClose={onClose} />
                        <Daily daily={d.daily} />
                        <hr className="border-slate-100" />
                        <Modules modules={d.modules} />
                        <hr className="border-slate-100" />
                        <Recent recent={d.recent} />
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
};
